import {
    CardBody,
    RightContent,
    SponsorshipBox,
    FlexColumnFirst,
    CarrouselColumn,
    TagMaster,
    Tag,
    LogoContainer,
    Logo,
    FoggyLayerLeft,
    FoggyLayerRight,
    CoberturasEspeciaisImage,
    TextContainer,
    ImageContainer
} from './styles'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'
import { Autoplay } from 'swiper/modules'
import { useEffect, useRef, useState } from 'react'
import { relationQuote, SponsorType } from '@App/libs/utils/geral'
import {
    BannerCardProps,
    CoberturasEspeciaisProps,
    JurisPrudenteProps,
    PatrocinadoPorProps
} from '@App/libs/components/jurisprudente-card/types'
import { PatrocinioMaster } from '@App/libs/components/CoberturasEspeciaisLogos'

const NUMBER_OF_LOGOS_FIT_IN_CONTAINER = 3

/**
 * BannerCard component renders a banner with optional images and logos,
 * including a carousel for displaying sponsors and other related content.
 *
 * @param {BannerCardProps} props The component props containing data like image, logos, and header information.
 * @return {JSX.Element} The rendered BannerCard component.
 */
export default function BannerCard(props: Readonly<BannerCardProps>) {
    const {
        image,
        headerName,
        headerDescription,
        relationType,
        logo,
        patrocinadoresMasterLogo,
        patrocinadoPorLogo
    } = props
    const divRef = useRef<HTMLDivElement | null>(null)
    const [size, setSize] = useState({ width: 0, height: 0 })
    const [logoSizeIsBigger, setLogoSizeIsBigger] = useState(false)

    /**
     * Sets up a ResizeObserver to monitor changes in the size of the container.
     * When the size changes, the component's state is updated.
     */
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            if (divRef.current) {
                const { width, height } = divRef.current.getBoundingClientRect()
                setSize({ width, height })
            }
        })

        if (divRef.current) {
            resizeObserver.observe(divRef.current)
        }

        return () => {
            if (divRef.current) {
                resizeObserver.unobserve(divRef.current)
            }
        }
    }, [])

    /**
     * Checks if the total width of the logos exceeds the available width of the container.
     * If it does, it sets the logo size to be too big.
     */
    useEffect(() => {
        if (patrocinadoPorLogo && patrocinadoPorLogo.length > 1) {
            const logoBoxWidth = 80
            const logoBoxMarginRight = 16
            const numberOfLogos = patrocinadoPorLogo.length
            const logosContentSize =
                (logoBoxWidth + logoBoxMarginRight) * numberOfLogos

            setLogoSizeIsBigger(logosContentSize > size.width)
        }

        return () => {
            setLogoSizeIsBigger(false)
        }
    }, [size, patrocinadoPorLogo])

    /**
     * Returns the appropriate class name based on the number of logos in the container.
     *
     * @param {number} logoLength The number of logos in the container.
     * @return {string} The class name for the logo container.
     */
    const getLogoContainerClassName = (logoLength: number) => {
        if (logoLength >= NUMBER_OF_LOGOS_FIT_IN_CONTAINER)
            return 'SponsorshipBox-3'
        return 'SponsorshipBox-default'
    }

    return (
        <CardBody>
            {image && (
                <ImageContainer>
                    <Image
                        src={image}
                        alt={headerName ?? 'Cobertura Especiais'}
                        width={480}
                        height={320}
                        objectFit='cover'
                        style={{ borderRadius: '4px' }}
                    />
                </ImageContainer>
            )}
            <RightContent>
                <TextContainer>
                    <h1>{headerName}</h1>
                    <h2>{headerDescription}</h2>
                </TextContainer>
                <SponsorshipBox
                    className={
                        patrocinadoresMasterLogo &&
                        getLogoContainerClassName(
                            patrocinadoresMasterLogo.length
                        )
                    }
                >
                    {logo && relationType && (
                        <CoberturasEspeciais
                            relationType={relationType}
                            logo={logo}
                            headerName={headerName}
                        />
                    )}
                    {/* JURISPRUDENTE - PATROCINADORES MASTER */}
                    {patrocinadoresMasterLogo &&
                        patrocinadoresMasterLogo?.length > 0 && (
                            <PatrocinadoresMaster
                                patrocinadoresMasterLogo={
                                    patrocinadoresMasterLogo
                                }
                            />
                        )}
                    {/* JURISPRUDENTE - PATROCINADOR POR */}
                    {patrocinadoPorLogo && patrocinadoPorLogo.length > 0 && (
                        <PatrocinadoPor
                            divRef={divRef}
                            logoSizeIsBigger={logoSizeIsBigger}
                            patrocinadoPorLogo={patrocinadoPorLogo}
                            containerWidth={size.width}
                        />
                    )}
                </SponsorshipBox>
            </RightContent>
        </CardBody>
    )
}

/**
 * CoberturasEspeciais component renders the special coverage logo with its related information.
 *
 * @param {CoberturasEspeciaisProps} props The component props containing relation type, logo, and header name.
 * @return {JSX.Element} The rendered CoberturasEspeciais component.
 */
function CoberturasEspeciais({
    relationType,
    logo,
    headerName
}: Readonly<CoberturasEspeciaisProps>) {
    return (
        <CoberturasEspeciaisImage>
            <p>{relationQuote(relationType as SponsorType)}</p>
            <Logo>
                <div className='img-container'>
                    <Image
                        src={logo}
                        alt={headerName ?? 'Cobertura Especiais'}
                        width={80}
                        height={80}
                    />
                </div>
            </Logo>
        </CoberturasEspeciaisImage>
    )
}

/**
 * PatrocinadoresMaster component renders the master sponsors logos inside a carousel.
 *
 * @param {JurisPrudenteProps} props The component props containing the master sponsor logos.
 * @return {JSX.Element} The rendered PatrocinadoresMaster component.
 */
function PatrocinadoresMaster({
    patrocinadoresMasterLogo
}: Readonly<JurisPrudenteProps>) {
    return (
        <FlexColumnFirst className='FlexColumnFirst'>
            <TagMaster label={PatrocinioMaster}>
                <Image
                    src={'/images/Circle.svg'}
                    alt={'icon'}
                    width={16}
                    height={16}
                    style={{ borderRadius: '4px' }}
                />
                <p>Patrocínio master</p>
            </TagMaster>
            <LogoContainer>
                <Swiper
                    slidesPerView={'auto'}
                    spaceBetween={16}
                    autoplay={{
                        delay: 0,
                        disableOnInteraction: true,
                        stopOnLastSlide: false,
                        waitForTransition: true
                    }}
                    effect='slide'
                    direction='horizontal'
                    freeMode={true}
                    speed={5000}
                    loop={true}
                    modules={
                        patrocinadoresMasterLogo.length >
                        NUMBER_OF_LOGOS_FIT_IN_CONTAINER
                            ? [Autoplay]
                            : []
                    }
                    className='mySwiper'
                >
                    {patrocinadoresMasterLogo?.map((item, index) => (
                        <Logo key={item.logo + index}>
                            <SwiperSlide>
                                <div className='img-container'>
                                    <Image
                                        src={item.logo}
                                        alt={'Patrocínios Maste Logo'}
                                        width={80}
                                        height={80}
                                    />
                                </div>
                            </SwiperSlide>
                        </Logo>
                    ))}
                </Swiper>
            </LogoContainer>
        </FlexColumnFirst>
    )
}

/**
 * PatrocinadoPor component renders the sponsored logos inside a carousel.
 *
 * @param {PatrocinadoPorProps} props The component props containing the reference, logo size state, sponsor logos, and container width.
 * @return {JSX.Element} The rendered PatrocinadoPor component.
 */
function PatrocinadoPor({
    divRef,
    logoSizeIsBigger,
    patrocinadoPorLogo,
    containerWidth
}: Readonly<PatrocinadoPorProps>) {
    return (
        <CarrouselColumn ref={divRef}>
            <Tag>
                <p className='grayDark'>Patrocinadores</p>
            </Tag>
            <LogoContainer
                className={'swiperContainer'}
                style={{ width: `${containerWidth}px` }}
            >
                {logoSizeIsBigger && <FoggyLayerLeft />}
                <Swiper
                    slidesPerView={'auto'}
                    spaceBetween={16}
                    autoplay={{
                        delay: 0,
                        disableOnInteraction: true,
                        stopOnLastSlide: false,
                        waitForTransition: true
                    }}
                    effect='slide'
                    direction='horizontal'
                    freeMode={true}
                    speed={5000}
                    loop={true}
                    modules={[Autoplay]}
                    className='mySwiper'
                >
                    {patrocinadoPorLogo?.map((item, index) => (
                        <SwiperSlide key={item.logo + index}>
                            <div className='img-container'>
                                <img src={item.logo} alt={item.name} />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
                {logoSizeIsBigger && <FoggyLayerRight />}
            </LogoContainer>
        </CarrouselColumn>
    )
}
